import { dayjsWithTimezone, DD_FORMAT, MMM_FORMAT, twClassMerge, YYYY_FORMAT } from '@healthhub/react-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { OutlinedVcIcon } from '@shared/assets-icon/Outlined';
import type { ClinicInformationType } from '@shared/services/appointment/getLists/types/getAppointmentList.type';
import { formattedDateDisplay, getDayOfWeekByNumber, getHourFormat } from '@shared/utils/date/dateUtil';

interface VcCardInformationProps {
  clinicInformation: ClinicInformationType;
  isRegistered?: boolean;
  isTextSizeIncreased?: boolean;
}

const VcCardInformation = ({
  clinicInformation,
  isTextSizeIncreased = false,
  isRegistered = false,
}: VcCardInformationProps) => {
  const { t } = useTranslation('appointments');
  const { institutionName, time, apptDate, apptTime, service } = clinicInformation;

  const apptDisplayDate = useMemo(() => {
    if (!apptDate && !apptTime) return time;
    return apptDate;
  }, [apptDate, apptTime, time]);

  return (
    <div className={twClassMerge('flex gap-4', { 'flex-col sm:flex-row': isTextSizeIncreased })}>
      {!!apptDisplayDate && (
        <div
          className={twClassMerge(
            'flex h-fit min-w-[3.475rem] flex-col items-center justify-center rounded-2xl bg-primary-cyan text-center p-1 gap-1',
            {
              'sm:flex-col flex-row text-left sm:text-center justify-start sm:justify-center p-1.5 sm:p-1':
                isTextSizeIncreased,
            }
          )}
        >
          <h3 className="flex items-center h3 font-bold text-theme-700">
            {formattedDateDisplay(apptDisplayDate, DD_FORMAT)}
          </h3>
          <span className="span font-semibold text-theme-700">{formattedDateDisplay(apptDisplayDate, MMM_FORMAT)}</span>
          <span className="span font-semibold text-theme-700">
            {formattedDateDisplay(apptDisplayDate, YYYY_FORMAT)}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-2">
          <OutlinedVcIcon className="size-5 shrink-0" />
          <p className="p1 font-semibold wrap-break-word">
            {isRegistered ? t('appointment.videoConsultation') : (service || t('appointment.videoConsultation'))}
          </p>
        </div>
        {!!apptDate && !!apptTime ? (
          <p className="p1 font-semibold text-secondary-blue-400 md:text-base">{apptTime}</p>
        ) : (
          !!time && (
            <p className="p1 font-semibold text-secondary-blue-400 md:text-base">{`${getDayOfWeekByNumber(
              dayjsWithTimezone(time).day()
            )}, ${getHourFormat(time)}`}</p>
          )
        )}
        {!!institutionName && (
          <p className="p2 font-normal text-functional-iron md:text-sm">
            {t('appointment.videoConsultationBy')}
            {institutionName}
          </p>
        )}
      </div>
    </div>
  );
};

export default VcCardInformation;
